import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import {
	MaterialReactTable,
	useMaterialReactTable,
} from "material-react-table";
import { MRT_Localization_PT_BR } from "material-react-table/locales/pt-BR";
import "./TableContainer.css";

/**
 * DynamicMaterialTableWithNoFilters - Componente de tabela sem filtros com suporte a larguras em porcentagem/pixels
 *
 * LARGURAS EM PORCENTAGEM:
 * Para usar larguras em porcentagem nas colunas, adicione as seguintes propriedades ao objeto da coluna:
 * - widthPercentage: número (ex: 25 para 25% da largura total)
 * - minWidthPercentage: número (largura mínima em %)
 * - maxWidthPercentage: número (largura máxima em %)
 *
 * LARGURAS EM PIXELS:
 * Para usar larguras em pixels nas colunas, adicione as seguintes propriedades ao objeto da coluna:
 * - widthPixels: número (ex: 200 para 200px)
 * - minWidthPixels: número (largura mínima em pixels)
 * - maxWidthPixels: número (largura máxima em pixels)
 *
 * RESPONSIVIDADE AUTOMÁTICA:
 * A tabela se ajusta automaticamente baseada no tamanho da tela:
 * - Mobile (< 768px): Base de 500px
 * - Tablet (768px - 1024px): Base de 700px
 * - Laptop pequeno (1024px - 1366px): Base de 900px
 * - Laptop médio (1366px - 1500px): Base de 1000px
 * - Desktop (1500px - 1920px): Base de 1100px
 * - Telas grandes (> 1920px): Base de 1200px
 */

const DynamicMaterialTableWithNoFilters = ({ columns, data }) => {
	const [screenWidth, setScreenWidth] = useState(window.innerWidth);

	// Hook para detectar mudanças no tamanho da tela
	useEffect(() => {
		const handleResize = () => {
			setScreenWidth(window.innerWidth);
		};

		// Detectar mudanças de zoom também
		const handleZoom = () => {
			setScreenWidth(window.innerWidth);
		};

		window.addEventListener("resize", handleResize);
		window.addEventListener("zoom", handleZoom);
		
		return () => {
			window.removeEventListener("resize", handleResize);
			window.removeEventListener("zoom", handleZoom);
		};
	}, []);

	// Função para calcular a base de pixels baseada no tamanho da tela
	const getBaseWidth = () => {
		// Usar uma base menor para evitar overflow com zoom
		const availableWidth = window.innerWidth * 0.9; // 90% da largura da tela
		if (screenWidth < 768) return Math.min(availableWidth, 500);
		if (screenWidth < 1024) return Math.min(availableWidth, 700);
		if (screenWidth < 1366) return Math.min(availableWidth, 900);
		if (screenWidth < 1500) return Math.min(availableWidth, 1000);
		if (screenWidth < 1920) return Math.min(availableWidth, 1100);
		return Math.min(availableWidth, 1200);
	};

	// Função para calcular larguras automáticas se não especificadas
	const calculateColumnWidths = (columns) => {
		const columnsWithPercentage = columns.filter(
			(col) => col.widthPercentage,
		);
		const columnsWithoutPercentage = columns.filter(
			(col) => !col.widthPercentage,
		);

		if (
			columnsWithoutPercentage.length > 0 &&
			columnsWithPercentage.length > 0
		) {
			const usedPercentage = columnsWithPercentage.reduce(
				(sum, col) => sum + col.widthPercentage,
				0,
			);
			const remainingPercentage = 100 - usedPercentage;
			const autoWidth =
				remainingPercentage / columnsWithoutPercentage.length;

			return columns.map((col) => ({
				...col,
				widthPercentage: col.widthPercentage || autoWidth,
			}));
		}

		return columns;
	};

	// Aplicar cálculo automático se necessário
	const columnsWithCalculatedWidths = calculateColumnWidths(columns);

	// Processar colunas para converter widthPercentage/widthPixels em tamanhos responsivos
	const processedColumns = columnsWithCalculatedWidths.map((col) => {
		const baseWidth = getBaseWidth(); // Usar base dinâmica e flexível
		
		// Suporte para larguras em pixels
		if (col.widthPixels || col.minWidthPixels || col.maxWidthPixels) {			
			return {
				...col,
				// Usar valores diretos em pixels
				size: col.widthPixels || col.size || undefined,
				minSize: col.minWidthPixels || col.minSize || undefined,
				maxSize: col.maxWidthPixels || col.maxSize || undefined,
			};
		}
		
		// Suporte para larguras em porcentagem (funcionalidade existente)
		const convertedSize = col.widthPercentage
			? Math.round((col.widthPercentage / 100) * baseWidth)
			: col.size || undefined;


		return {
			...col,
			// Usar as propriedades nativas da MaterialReactTable
			size: convertedSize,
			minSize: col.minWidthPercentage
				? Math.round((col.minWidthPercentage / 100) * baseWidth)
				: col.minSize || undefined,
			maxSize: col.maxWidthPercentage
				? Math.round((col.maxWidthPercentage / 100) * baseWidth)
				: col.maxSize || undefined,
		};
	});

	const table = useMaterialReactTable({
		columns: processedColumns,
		data,
		localization: MRT_Localization_PT_BR,
		muiFilterTextFieldProps: {
			size: "small",
			variant: "outlined",
		},
		muiTableHeadCellProps: {
			sx: {
				fontSize: "0.8rem",
				padding: "2px 7px",
				height: "30px",
			},
		},
		muiTableBodyCellProps: {
			sx: {
				fontSize: "0.8rem",
				padding: "15px 7px",
			},
		},
		muiTableProps: {
			sx: {
				width: "100%",
				tableLayout: "fixed",
				overflowX: "auto",
				maxWidth: "100%",
				"@media (max-width: 768px)": {
					fontSize: "0.8rem",
				},
			},
		},
		muiTableBodyRowProps: ({ row }) => ({
			sx: {
				backgroundColor:
					row.original.periodic == 1
						? "#f1b44c"
						: row.index % 2 === 0
						? "#F9F9F9"
						: "#fff",
			},
		}),
		muiTableHeadProps: {
			sx: {
				height: "30px", // Reduzido de 35px para 30px
				"& .MuiTableRow-root": {
					height: "30px", // Reduzido de 35px para 30px
					minHeight: "30px",
				},
			},
		},
		enableTopToolbar: false,
		enablePagination: false,
		enableBottomToolbar: false,
	});

	return <MaterialReactTable table={table} />;
};

DynamicMaterialTableWithNoFilters.propTypes = {
	columns: PropTypes.array.isRequired,
	data: PropTypes.array.isRequired,
};

export default DynamicMaterialTableWithNoFilters;
